import recintosData from '@/data/recintos/recintos-electorales-2027.json'
import type { InferredTerritory } from '@/lib/data/territoryLocation'
import type {
  AdministrativeLevel,
  CantonCatalogItem,
  ParroquiaCatalogItem,
  VotingPlace,
  VotingPlaceCatalogs,
  VotingPlaceFilters,
  ZonaCatalogItem,
} from '@/types/votingPlace'

type RecintosPayload = {
  meta: Record<string, unknown>
  catalogos: VotingPlaceCatalogs
  recintos: VotingPlace[]
}

const payload = recintosData as RecintosPayload

export const recintos = payload.recintos
export const recintosCatalogos = payload.catalogos
export const recintosMeta = payload.meta
const recintosById = new Map(recintos.map((recinto) => [recinto.id, recinto]))
let recintosGeoJsonCache: GeoJSON.FeatureCollection | null = null

export interface VotingPlaceLocationMatch {
  filters: VotingPlaceFilters
  selectedRecintoId: string
  label: string
}

export function getRecintoById(id: string) {
  return recintosById.get(id) ?? null
}

export function getVotingPlaces() {
  return recintos
}

export function getVotingPlaceMeta() {
  return recintosMeta
}

export function getVotingPlaceCatalogs(filters: VotingPlaceFilters = {}) {
  const provincias = recintosCatalogos.provincias as AdministrativeLevel[]
  const cantones = recintosCatalogos.cantones as CantonCatalogItem[]
  const parroquias = recintosCatalogos.parroquias as ParroquiaCatalogItem[]
  const zonas = recintosCatalogos.zonas as ZonaCatalogItem[]

  return {
    provincias,
    cantones: filters.provincia
      ? cantones.filter((canton) => canton.provinciaCodigo === filters.provincia)
      : [],
    parroquias: filters.provincia && filters.canton
      ? parroquias.filter(
          (parroquia) =>
            parroquia.provinciaCodigo === filters.provincia &&
            parroquia.cantonCodigo === filters.canton,
        )
      : [],
    zonas: filters.provincia && filters.canton && filters.parroquia
      ? zonas.filter(
          (zona) =>
            zona.provinciaCodigo === filters.provincia &&
            zona.cantonCodigo === filters.canton &&
            zona.parroquiaCodigo === filters.parroquia,
        )
      : [],
  } satisfies VotingPlaceCatalogs
}

export function getVotingPlaceCantonFromFilters(filters: VotingPlaceFilters) {
  if (!filters.canton) return null

  return (
    recintosCatalogos.cantones.find(
      (canton) =>
        canton.codigo === filters.canton &&
        (!filters.provincia || canton.provinciaCodigo === filters.provincia),
    ) ?? null
  )
}

export function getVotingPlaceProvinceFromFilters(filters: VotingPlaceFilters) {
  if (!filters.provincia) return null

  return (
    recintosCatalogos.provincias.find(
      (province) => province.codigo === filters.provincia,
    ) ?? null
  )
}

export function getVotingPlaceProvinceSlugOptions() {
  return recintosCatalogos.provincias
    .map((province) => ({
      id: province.slug,
      label: province.nombre,
      codigo: province.codigo,
    }))
    .sort((a, b) => a.label.localeCompare(b.label, 'es'))
}

export function getVotingPlaceCantonSlugOptions(provinceSlug?: string) {
  const province = provinceSlug
    ? recintosCatalogos.provincias.find((item) => item.slug === provinceSlug)
    : null

  return recintosCatalogos.cantones
    .filter((canton) => !province || canton.provinciaCodigo === province.codigo)
    .map((canton) => {
      const cantonProvince =
        province ??
        recintosCatalogos.provincias.find((item) => item.codigo === canton.provinciaCodigo)

      return {
        id: canton.slug,
        label: canton.nombre,
        provinceId: cantonProvince?.slug ?? '',
        codigo: canton.codigo,
      }
    })
    .sort((a, b) => a.label.localeCompare(b.label, 'es'))
}

export function filterVotingPlaces(filters: VotingPlaceFilters) {
  if (!filters.provincia && !filters.canton && !filters.parroquia && !filters.zona) {
    return recintos
  }

  return recintos.filter((recinto) => {
    if (
      filters.provincia &&
      recinto.ubicacion.provincia.codigo !== filters.provincia
    ) return false
    if (
      filters.canton &&
      recinto.ubicacion.canton.codigo !== filters.canton
    ) return false
    if (
      filters.parroquia &&
      recinto.ubicacion.parroquia.codigo !== filters.parroquia
    ) return false
    if (
      filters.zona &&
      recinto.ubicacion.zona.codigo !== filters.zona
    ) return false
    return true
  })
}

export function getVotingPlaceLocationMatch(
  territory: InferredTerritory | null,
): VotingPlaceLocationMatch | null {
  if (!territory) return null

  const nearestRecinto = getRecintoById(territory.nearestRecintoId)
  const matchedRecinto =
    nearestRecinto ??
    recintos.find(
      (recinto) =>
        recinto.ubicacion.provincia.slug === territory.provinceId &&
        recinto.ubicacion.canton.slug === territory.cantonId,
    )

  if (!matchedRecinto) return null

  return {
    filters: {
      provincia: matchedRecinto.ubicacion.provincia.codigo,
      canton: matchedRecinto.ubicacion.canton.codigo,
      parroquia: matchedRecinto.ubicacion.parroquia.codigo,
    },
    selectedRecintoId: matchedRecinto.id,
    label: `${matchedRecinto.ubicacion.canton.nombre}, ${matchedRecinto.ubicacion.provincia.nombre}`,
  }
}

export async function getRecintosGeoJson() {
  if (recintosGeoJsonCache) return recintosGeoJsonCache

  const module = await import('@/data/recintos/recintos-electorales-2027.geojson?raw')
  recintosGeoJsonCache = JSON.parse(module.default) as GeoJSON.FeatureCollection
  return recintosGeoJsonCache
}

export function filterRecintosGeoJson(
  source: GeoJSON.FeatureCollection,
  recintoIds: Set<string>,
) {
  return {
    ...source,
    features: source.features.filter((feature) => {
      const id = String(feature.id ?? feature.properties?.id ?? '')
      return recintoIds.has(id)
    }),
  } satisfies GeoJSON.FeatureCollection
}

export function getRecintosDatasetStats() {
  return {
    totalRecintos: recintos.length,
    totalProvincias: recintosCatalogos.provincias.length,
    totalCantones: recintosCatalogos.cantones.length,
    totalParroquias: recintosCatalogos.parroquias.length,
    totalZonas: recintosCatalogos.zonas.length,
    geoJsonLoaded: Boolean(recintosGeoJsonCache),
  }
}
